import * as Y from 'yjs';

import type {
    YSlateBlock,
    YSlateDocument,
    YSlateFragment,
    YSlateInline,
    YSlateNode,
} from './utils';

/**
 * This function is equivalent to node.assertPath(path) but for the Y.js tree.
 */
export function lookupByPath(root: YSlateDocument, path: readonly number[]): YSlateNode {
    let node: YSlateNode = root;
    for (let i = 0; i < path.length; i++) {
        const index = path[i];

        if (node instanceof Y.XmlText) {
            throw new Error('Invalid path, expected a node with descendants');
        }

        if (index < node.length) {
            node = node.get(index);
        } else {
            const fragments = (node as YSlateBlock).getAttribute('fragments');
            if (fragments) {
                node = fragments.get(index - node.length);
            }
        }

        if (!node) {
            throw new Error('Invalid path, expected a node');
        }
    }
    return node;
}

export function lookupParentByPath(
    root: YSlateDocument,
    path: readonly number[]
): { parent: YSlateDocument | YSlateBlock | YSlateInline | YSlateFragment; index: number } {
    const parentPath = path.slice(0, -1);
    const parent = lookupByPath(root, parentPath);
    if (parent instanceof Y.XmlText) {
        throw new Error('Invalid path, expected a node with descendants');
    }

    const index = path[path.length - 1];
    return { parent, index };
}

export function lookupValueInData(node: YSlateInline | YSlateBlock, keys: string[]): Y.Map<any> {
    let rootData = node.getAttribute('data');
    if (!rootData) {
        rootData = new Y.Map();
        node.setAttribute('data', rootData);
    }

    let value = rootData;
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        const parent = value;
        const child = parent.get(key);

        if (child === undefined) {
            const [insert, descendant] = createMapTree(keys.slice(i + 1));
            parent.set(key, insert);
            return descendant;
        }
        if (!(child instanceof Y.Map)) {
            throw new Error(
                `Invalid data path (${keys.slice(0, i + 1).join('.')}), expected a map`
            );
        }
        value = child;
    }
    return value;
}

function createMapTree(key: string[]): [Y.Map<any>, Y.Map<any>] {
    const map = new Y.Map();
    let node = map;
    for (let i = 0; i < key.length; i++) {
        const value = new Y.Map();
        node.set(key[i], value);
        node = value;
    }
    return [map, node];
}
